/**
 * Native Excel chart injector.
 *
 * `exceljs` (the library used to build the workbook) does not support writing
 * native Excel charts. To get an editable chart in the downloaded `.xlsx`
 * (one whose bars and data range you can click and edit in Excel), we
 * post-process the generated workbook ZIP and inject the OOXML chart parts
 * by hand.
 *
 * Workflow:
 *   1. The caller adds a 1x1 transparent placeholder image to the target
 *      sheet via `worksheet.addImage(...)`. This makes ExcelJS create the
 *      drawing infrastructure (drawing1.xml, sheet -> drawing relationship,
 *      drawing content type, `<drawing r:id="..."/>` element on the sheet).
 *   2. The caller writes the workbook to a buffer.
 *   3. We pass that buffer to `injectNativeColumnChart`, which:
 *        - Replaces `xl/drawings/drawing1.xml` with a chart `graphicFrame`.
 *        - Replaces `xl/drawings/_rels/drawing1.xml.rels` so the drawing now
 *          points at `xl/charts/chart1.xml` instead of the placeholder image.
 *        - Adds `xl/charts/chart1.xml` with the column chart definition.
 *        - Adds the chart content-type override to `[Content_Types].xml`.
 *
 * The orphaned placeholder image is left in the ZIP — Excel ignores parts
 * that aren't referenced by anything.
 */

import JSZip from 'jszip';

/** 1x1 transparent PNG (base64). Used as a placeholder addImage source. */
export const PLACEHOLDER_PNG_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';

function escapeXml(unsafe) {
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Sheet names with apostrophes need them doubled inside formula references. */
function sheetRefName(sheetName) {
  return sheetName.replace(/'/g, "''");
}

function buildChartXml({
  sheetName,
  title,
  color,
  rows,
  valueLabel,
  max,
  majorUnit,
}) {
  const safeSheet = sheetRefName(sheetName);
  const lastRow = rows.length + 1;
  const catRef = `'${safeSheet}'!$A$2:$A$${lastRow}`;
  const valRef = `'${safeSheet}'!$B$2:$B$${lastRow}`;
  const titleRef = `'${safeSheet}'!$B$1`;

  const catCache = rows
    .map(
      (r, i) =>
        `<c:pt idx="${i}"><c:v>${escapeXml(r.label)}</c:v></c:pt>`,
    )
    .join('');

  const valCache = rows
    .map(
      (r, i) =>
        `<c:pt idx="${i}"><c:v>${Number(r.value).toFixed(4)}</c:v></c:pt>`,
    )
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:chart>
    <c:title>
      <c:tx>
        <c:rich>
          <a:bodyPr rot="0" spcFirstLastPara="1" vertOverflow="ellipsis" wrap="square" anchor="ctr" anchorCtr="1"/>
          <a:lstStyle/>
          <a:p>
            <a:pPr>
              <a:defRPr sz="1600" b="1" kern="1200">
                <a:solidFill><a:srgbClr val="1F2937"/></a:solidFill>
                <a:latin typeface="Calibri"/>
              </a:defRPr>
            </a:pPr>
            <a:r>
              <a:rPr lang="en-US" sz="1600" b="1">
                <a:solidFill><a:srgbClr val="1F2937"/></a:solidFill>
              </a:rPr>
              <a:t>${escapeXml(title)}</a:t>
            </a:r>
          </a:p>
        </c:rich>
      </c:tx>
      <c:overlay val="0"/>
    </c:title>
    <c:autoTitleDeleted val="0"/>
    <c:plotArea>
      <c:layout/>
      <c:barChart>
        <c:barDir val="col"/>
        <c:grouping val="clustered"/>
        <c:varyColors val="0"/>
        <c:ser>
          <c:idx val="0"/>
          <c:order val="0"/>
          <c:tx>
            <c:strRef>
              <c:f>${titleRef}</c:f>
              <c:strCache>
                <c:ptCount val="1"/>
                <c:pt idx="0"><c:v>${escapeXml(valueLabel)}</c:v></c:pt>
              </c:strCache>
            </c:strRef>
          </c:tx>
          <c:spPr>
            <a:solidFill><a:srgbClr val="${color}"/></a:solidFill>
            <a:ln><a:noFill/></a:ln>
          </c:spPr>
          <c:invertIfNegative val="0"/>
          <c:cat>
            <c:strRef>
              <c:f>${catRef}</c:f>
              <c:strCache>
                <c:ptCount val="${rows.length}"/>
                ${catCache}
              </c:strCache>
            </c:strRef>
          </c:cat>
          <c:val>
            <c:numRef>
              <c:f>${valRef}</c:f>
              <c:numCache>
                <c:formatCode>0.00</c:formatCode>
                <c:ptCount val="${rows.length}"/>
                ${valCache}
              </c:numCache>
            </c:numRef>
          </c:val>
        </c:ser>
        <c:gapWidth val="150"/>
        <c:axId val="111111111"/>
        <c:axId val="222222222"/>
      </c:barChart>
      <c:catAx>
        <c:axId val="111111111"/>
        <c:scaling><c:orientation val="minMax"/></c:scaling>
        <c:delete val="0"/>
        <c:axPos val="b"/>
        <c:crossAx val="222222222"/>
        <c:crosses val="autoZero"/>
        <c:auto val="1"/>
        <c:lblAlgn val="ctr"/>
        <c:lblOffset val="100"/>
        <c:noMultiLvlLbl val="0"/>
      </c:catAx>
      <c:valAx>
        <c:axId val="222222222"/>
        <c:scaling>
          <c:orientation val="minMax"/>
          <c:max val="${max}"/>
          <c:min val="0"/>
        </c:scaling>
        <c:delete val="0"/>
        <c:axPos val="l"/>
        <c:crossAx val="111111111"/>
        <c:crosses val="autoZero"/>
        <c:crossBetween val="between"/>
        <c:majorUnit val="${majorUnit}"/>
      </c:valAx>
    </c:plotArea>
    <c:plotVisOnly val="1"/>
    <c:dispBlanksAs val="gap"/>
  </c:chart>
</c:chartSpace>`;
}

function buildChartDrawingXml({ fromCol, fromRow, toCol, toRow }) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart">
  <xdr:twoCellAnchor>
    <xdr:from>
      <xdr:col>${fromCol}</xdr:col>
      <xdr:colOff>0</xdr:colOff>
      <xdr:row>${fromRow}</xdr:row>
      <xdr:rowOff>0</xdr:rowOff>
    </xdr:from>
    <xdr:to>
      <xdr:col>${toCol}</xdr:col>
      <xdr:colOff>0</xdr:colOff>
      <xdr:row>${toRow}</xdr:row>
      <xdr:rowOff>0</xdr:rowOff>
    </xdr:to>
    <xdr:graphicFrame macro="">
      <xdr:nvGraphicFramePr>
        <xdr:cNvPr id="2" name="Chart 1"/>
        <xdr:cNvGraphicFramePr/>
      </xdr:nvGraphicFramePr>
      <xdr:xfrm>
        <a:off x="0" y="0"/>
        <a:ext cx="0" cy="0"/>
      </xdr:xfrm>
      <a:graphic>
        <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">
          <c:chart r:id="rId1"/>
        </a:graphicData>
      </a:graphic>
    </xdr:graphicFrame>
    <xdr:clientData/>
  </xdr:twoCellAnchor>
</xdr:wsDr>`;
}

function buildDrawingRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart1.xml"/>
</Relationships>`;
}

/**
 * Inject a native, editable column chart into an ExcelJS-generated workbook.
 *
 * Requirements before calling:
 *   - The target sheet must already contain a placeholder image added via
 *     `worksheet.addImage(...)` (use `PLACEHOLDER_PNG_BASE64`). This causes
 *     ExcelJS to set up `xl/drawings/drawing1.xml`, the worksheet ->
 *     drawing relationship, and the drawing content type for us.
 *   - The data backing the chart must live on `sheetName` with the layout:
 *       row 1: header (column B header is used as the series name).
 *       rows 2..N+1: A = category label, B = numeric value.
 *
 * @param {ArrayBuffer|Uint8Array} buffer  Raw bytes from `workbook.xlsx.writeBuffer()`.
 * @param {object}    options
 * @param {string}    options.sheetName   Sheet that holds the chart data.
 * @param {string}    options.title       Chart title rendered above the plot.
 * @param {string}   [options.valueLabel] Series name (defaults to "Value").
 * @param {string}   [options.color]      Bar fill (RGB hex w/o "#"); default "5B9BD5".
 * @param {{label:string, value:number}[]} options.rows  Data rows in order.
 * @param {number}   [options.max]        y-axis max; default 5.
 * @param {number}   [options.majorUnit]  y-axis tick step; default 0.5.
 * @param {number}   [options.fromCol]    Top-left anchor column (0-based).
 * @param {number}   [options.fromRow]    Top-left anchor row (0-based).
 * @param {number}   [options.toCol]      Bottom-right anchor column (0-based).
 * @param {number}   [options.toRow]      Bottom-right anchor row (0-based).
 * @returns {Promise<ArrayBuffer>}        Modified workbook ZIP buffer.
 */
export async function injectNativeColumnChart(buffer, options) {
  const {
    sheetName,
    title = 'Chart',
    valueLabel = 'Value',
    color = '5B9BD5',
    rows = [],
    max = 5,
    majorUnit = 0.5,
    fromCol = 3,
    fromRow = 0,
    toCol = 13,
    toRow = 20,
  } = options;

  if (!rows.length) return buffer;

  const zip = await JSZip.loadAsync(buffer);

  // Replace the placeholder image's drawing with our chart drawing.
  // ExcelJS names the first drawing `drawing1.xml` and routes the sheet's
  // <drawing/> element to its rels file. We swap both contents so the same
  // drawing slot now references a chart instead of an image.
  zip.file(
    'xl/drawings/drawing1.xml',
    buildChartDrawingXml({ fromCol, fromRow, toCol, toRow }),
  );
  zip.file('xl/drawings/_rels/drawing1.xml.rels', buildDrawingRelsXml());

  zip.file(
    'xl/charts/chart1.xml',
    buildChartXml({
      sheetName,
      title,
      color,
      rows,
      valueLabel,
      max,
      majorUnit,
    }),
  );

  // Register the chart content type so Excel knows how to parse chart1.xml.
  const ctFile = zip.file('[Content_Types].xml');
  if (ctFile) {
    let ctXml = await ctFile.async('string');
    if (!ctXml.includes('xl/charts/chart1.xml')) {
      ctXml = ctXml.replace(
        '</Types>',
        '<Override PartName="/xl/charts/chart1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/></Types>',
      );
      zip.file('[Content_Types].xml', ctXml);
    }
  }

  return zip.generateAsync({
    type: 'arraybuffer',
    compression: 'DEFLATE',
  });
}
